// Battle Plan page (#/plan, battle design section 6): everything a player sets ahead of a fight, in one place, and the
// practice runs that test it. All of it is saved on the server and fights run without you:
// - Line-ups: up to 5 saved line-ups, which one the Arena fights with, and their order (the Arena's own editor).
// - Guild War: your war line-up (any saved line-up or your hunt team); every fighter hits by best match-up.
// - Practice: 20 fights of one of your line-ups against another of yours or a Hall Keeper's team, on practice seeds.
//   Line-up against line-up runs here in the page (the engine ships with the client); a Keeper's team runs on the
//   server, which holds the Keeper's rolls. No practice seed is ever a real fight's seed (shared/plan.ts).
// Per-creature tactics (burst, hold) are not offered: every one we measured lost 3-4 points against the auto-cast.
import { psecTabs, psecWrap } from './psec.ts';
import { ARENA, type ArenaCreature } from '../shared/arena.ts';
import { WAR } from '../shared/guild-war.ts';
import { PRACTICE, practice, type PracticeSummary } from '../shared/plan.ts';
import { HALLS, fitsHall, hallName, KEEPER_TITLE } from '../shared/halls.ts';
import { ELEMENTS, type Element } from '../shared/elements.ts';
import { asFighter } from '../shared/social.ts';
import { canFight } from '../shared/xp.ts';
import type { ClientMsg, CreatureDTO } from '../shared/protocol.ts';
import { S } from './state.ts';
import { esc } from './fmt.ts';
import { A, liveSynergy, lineupsHtml, loadArena, teamFace } from './arena.ts';
import { G } from './guild.ts';
import { H, loadHalls } from './halls.ts';
import { battleLogHtml } from './battlelog.ts';
import { synergyChips } from './synergy.ts';
import { heldBadge } from './held.ts';
import type { BoardRow } from '../shared/board.ts';
import { rowsFor } from '../shared/pvpfight.ts';
import { boardOn, pickFrom, shownRows } from './rows.ts';

export const P = {
  /** Your side: 'hunt' or 'l<i>' (a saved line-up). */
  mine: 'hunt',
  /** The other side: 'l<i>' (another of your line-ups) or 'h<element>' (a Hall Keeper). */
  vs: '',
  result: null as null | { label: string; r: PracticeSummary },
  waiting: false,
};

let send: (m: ClientMsg) => void = () => {};
let rerender: () => void = () => {};
export function bindPlan(o: { send: (m: ClientMsg) => void; render: () => void }) { send = o.send; rerender = o.render; }

export function loadPlan() { loadArena(); loadHalls(); send({ t: 'guildWar' }); }

/** A Keeper practice came back from the server. */
export function onHallPractice(m: { el: Element; result: PracticeSummary }) {
  P.waiting = false;
  P.result = { label: `against ${esc(hallName(m.el))}'s ${KEEPER_TITLE}`, r: m.result };
  rerender();
}

const usable = (ids: number[]) => ids.map((id) => S.creatures.get(id)).filter((c): c is CreatureDTO => !!c && !c.listed);
/** The creatures of a side choice, in order, as they would fight (one of each species, those that can fight at your level). */
function sideOf(key: string): { name: string; team: CreatureDTO[]; rows?: BoardRow[] } {
  const t = S.trainer!;
  const d = A.data;
  const i = Number(key.slice(1));
  const raw = key === 'hunt' ? usable(t.team) : key.startsWith('l') && d?.lineups[i] ? usable(d.lineups[i].ids) : [];
  const seen = new Set<string>();
  const team = raw.filter((c) => canFight(c, t.level) && !seen.has(c.species) && !!seen.add(c.species)).slice(0, ARENA.teamMax);
  // A saved line-up's rows (the board) by creature; the hunt team and a line-up without rows stand on defaultRows.
  const l = key.startsWith('l') ? d?.lineups[i] : undefined;
  const rows = l?.rows ? rowsFor(team.map((c) => c.id), l)?.map((r) => r ?? 'back') : undefined;
  return { name: key === 'hunt' ? 'Hunt team' : l?.name ?? 'Line-up', team, ...(rows ? { rows } : {}) };
}

export function planHead() {
  return { meta: '', actions: '<button class="btn small" data-act="plan-refresh">Refresh</button>' };
}

export function planPane(): string {
  const d = A.data;
  if (!d) return '<p class="hint">Loading your Battle Plan…</p>';
  const keys = ['lineups', 'war', 'practice'];
  return `${psecTabs('plan', [{ key: 'lineups', title: 'Line-ups' }, { key: 'war', title: 'Guild War' }, { key: 'practice', title: 'Practice' }])}<div class="plan-grid">
      <div class="plan-col">${psecWrap('plan', 'lineups', keys, lineupsHtml(d))}${psecWrap('plan', 'war', keys, warCard())}</div>
      <div class="plan-col">${psecWrap('plan', 'practice', keys, practiceCard())}</div>
    </div>`;
}

function warCard(): string {
  const w = G.war;
  const t = S.trainer!;
  if (!t.guild) return `<section class="panel plan-war"><h4>Guild War</h4><p class="hint">No guild.</p></section>`;
  if (!w) return '<section class="panel plan-war"><h4>Guild War</h4><p class="hint">Loading…</p></section>';
  const saved = usable(w.lineup);
  const fights = (saved.length ? saved : usable(t.team)).filter((c) => canFight(c, t.level)).slice(0, WAR.teamMax);
  const same = (ids: number[]) => w.lineup.length === ids.length && w.lineup.every((x, i) => x === ids[i]);
  const lineups = A.data?.lineups ?? [];
  return `<section class="panel plan-war"><div class="ar-l-head"><h4>Guild War</h4><span class="spacer"></span><span class="muted">${saved.length ? 'Saved war line-up' : 'Hunt team'}</span></div>
    <div class="ar-team">${fights.map((c, i) => teamFace(c, `<i>${i + 1}</i>`)).join('') || '<span class="muted">No creature can fight yet.</span>'}</div>
    <div class="actions plan-wl">${lineups.map((l, i) => `<button class="btn small${same(l.ids) ? ' on' : ''}" data-act="plan-war-use" data-id="${i}"${same(l.ids) ? ' aria-pressed="true"' : ''}>Use ${esc(l.name)}</button>`).join('')}
      <button class="btn small${saved.length ? '' : ' on'}" data-act="plan-war-hunt"${saved.length ? '' : ' aria-pressed="true"'}>Use hunt team</button><a class="link" href="#/guild">Edit on the Guild page</a></div>
  </section>`;
}

function practiceCard(): string {
  const d = A.data!;
  const t = S.trainer!;
  const mineOpts: [string, string][] = [['hunt', 'Hunt team'], ...d.lineups.map((l, i): [string, string] => [`l${i}`, l.name])];
  if (!mineOpts.some(([k]) => k === P.mine)) P.mine = 'hunt';
  const keepers = (H.data?.halls ?? []).filter((h) => h.keeper && !h.keeper.me);
  const vsOpts: [string, string][] = [
    ...d.lineups.map((l, i): [string, string] => [`l${i}`, `Your line-up: ${l.name}`]).filter(([k]) => k !== P.mine),
    ...keepers.map((h): [string, string] => [`h${h.el}`, `${hallName(h.el)}: ${h.keeper!.name}'s team`]),
  ];
  if (!vsOpts.some(([k]) => k === P.vs)) P.vs = vsOpts[0]?.[0] ?? '';
  const me = sideOf(P.mine);
  const meRows = boardOn() && me.team.length ? shownRows(me.team.map(asFighter), pickFrom(me.team.map((c) => c.id), me.rows)) : null;
  let them: { species: string; level: number; aura?: boolean }[] = [];
  let note = '';
  // Your own line-up shows its front row too; a Keeper's rows stay hidden, as in the Halls.
  const vsMine = P.vs.startsWith('l') ? sideOf(P.vs) : null;
  const themRows = boardOn() && vsMine?.team.length ? shownRows(vsMine.team.map(asFighter), pickFrom(vsMine.team.map((c) => c.id), vsMine.rows)) : null;
  if (vsMine) them = vsMine.team.map((c) => ({ species: c.species, level: c.level, aura: c.rolls.aura }));
  else if (P.vs.startsWith('h')) {
    const el = P.vs.slice(1) as Element;
    them = keepers.find((h) => h.el === el)?.keeper?.team ?? [];
    const off = me.team.filter((c) => !fitsHall(c.species, el));
    if (t.level < HALLS.unlockLevel) note = `Halls open at Lv ${HALLS.unlockLevel}`;
    else if (off.length) note = `${ELEMENTS[el].name} only: ${off.length} in this line-up ${off.length === 1 ? 'is' : 'are'} not`;
  }
  const ready = me.team.length > 0 && them.length > 0 && !note && !P.waiting;
  // Line-up against line-up fights on the Arena's rules (the ranked season's featured region); the Halls have none.
  const syn = vsMine ? liveSynergy() : undefined;
  const sel = (k: string, opts: [string, string][], v: string, label: string) => `<select data-plan="${k}" aria-label="${label}">${opts.map(([id, n]) => `<option value="${id}"${id === v ? ' selected' : ''}>${esc(n)}</option>`).join('')}</select>`;
  const r = P.result;
  return `<section class="panel plan-practice"><h4>Practice runs</h4>
    <div class="plan-vs"><label><span>You</span>${sel('mine', mineOpts, P.mine, 'Your side')}</label><div class="ar-team">${me.team.map((c, i) => teamFace(c, `<i>${i + 1}</i>${heldBadge(c)}`, false, meRows?.[i])).join('') || '<span class="muted">No creature can fight.</span>'}</div>${synergyChips(me.team, syn)}</div>
    <div class="plan-vs"><label><span>Against</span>${vsOpts.length ? sel('vs', vsOpts, P.vs, 'Opponent') : '<span class="muted">No opponents</span>'}</label><div class="ar-team">${them.map((c, i) => teamFace(c, `<i>${i + 1}</i>`, false, themRows?.[i])).join('')}</div>${synergyChips(them, syn)}</div>
    ${note ? `<p class="hint">${note}</p>` : ''}
    <div class="actions"><button class="btn primary" data-act="plan-practice"${ready ? '' : ' disabled'}>${P.waiting ? 'Running…' : `Run ${PRACTICE.runs} practice fights`}</button></div>
    ${r ? `<div class="plan-result"><p class="plan-score"><b class="${r.r.wins * 2 >= r.r.runs ? 'good-text' : 'bad-text'}">${r.r.wins} of ${r.r.runs}</b> won ${r.label} · average ${(r.r.avgMs / 1000).toFixed(0)} s · ${r.r.standing.toFixed(1)} standing${r.r.timeouts ? ` · ${r.r.timeouts} timed out` : ''}</p>
      <details><summary>The first practice fight, blow by blow</summary>${battleLogHtml(r.r.sample, ['You', r.r.sample.sides[1].name], 0)}</details></div>` : ''}
  </section>`;
}

/** Clicks on data-act="plan-…". */
export function planClick(el: HTMLElement): boolean {
  const act = el.dataset.act ?? '';
  if (!act.startsWith('plan-')) return false;
  const id = el.dataset.id ?? '';
  switch (act) {
    case 'plan-refresh': loadPlan(); return true;
    case 'plan-war-use': { const l = A.data?.lineups[Number(id)]; if (l) send({ t: 'guild', op: { k: 'warLineup', ids: l.ids.slice(0, WAR.teamMax) } }); return true; }
    case 'plan-war-hunt': send({ t: 'guild', op: { k: 'warLineup', ids: [] } }); return true;
    case 'plan-practice': {
      const me = sideOf(P.mine);
      if (P.vs.startsWith('h')) {
        P.waiting = true;
        send({ t: 'hallPractice', el: P.vs.slice(1), ids: me.team.map((c) => c.id), ...(me.rows ? { rows: me.rows } : {}) });
        break;
      }
      const them = sideOf(P.vs);
      const asA = (x: CreatureDTO[]): ArenaCreature[] => x.map(asFighter);
      P.result = { label: `against your ${esc(them.name)}`, r: practice({ name: me.name, team: asA(me.team), rows: me.rows }, { name: them.name, team: asA(them.team), rows: them.rows }, { fight: { ...(liveSynergy() ? { synergy: liveSynergy() } : {}) } }) };
      break;
    }
    default: return false;
  }
  rerender();
  return true;
}

/** The two side pickers. True when the page should redraw. */
export function planInput(el: HTMLInputElement | HTMLSelectElement): boolean {
  const k = el.dataset.plan;
  if (k === 'mine') { P.mine = el.value; P.result = null; return true; }
  if (k === 'vs') { P.vs = el.value; P.result = null; return true; }
  return false;
}

/** Help page section. */
export function planHelp(): string {
  return `<h4>Battle Plan</h4><p>The Battle Plan page (More menu) holds what every automatic fight reads: up to ${ARENA.lineups} saved line-ups and their order, which one the Arena fights with, and your Guild War line-up. Tournaments use the line-up you register, then any reorder you send between games. Practice runs fight ${PRACTICE.runs} times on the Arena's rules: one of your line-ups against another, or against a ${KEEPER_TITLE}'s team (in its public order). They use practice seeds, which no real fight ever uses: every real fight draws its own seed on the server when it starts, so practising shows your odds but can't show a result in advance. In the Guild War each of your creatures hits whichever of the enemy's front three its moves hit hardest by element.</p>`;
}

